import React, { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { pipe, Option } from 'effect';

import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import TruncatedTooltip from '@/components/mui/Tooltip/TruncatedTooltip';
import { MAX_VISIBLE_OPTIONS } from '@/components/mui/Form/Autocomplete/visibleOptions';
import AutocompleteVirtualizedBase from '@/components/mui/Form/Autocomplete/AutocompleteVirtualizedBase';
import { TextField } from '@/components/mui/Form/Autocomplete/components/styledComponents';
import { getOptionLogo, hasOptionLogos, withLogoSrc } from '@/lib/optionLogo';
import useBankBranchEntry from '@/hooks/useBankBranchEntry';
import type { BeneficiaryFieldOption } from '@/domain/payments/schemas';

import {
  getParentFieldName,
  getScopedOptions,
} from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/utils';
import type {
  BeneficiaryField,
  FormValues,
} from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/types';

// Per-field copy for "your search matched nothing". Keyed by field name because
// the message names the entity ("No banks matching…"), which the contract does
// not describe. Anything absent here falls back to the shared generic text.
// Always shows avatars, with a placeholder where `logo_url` is missing.
const BANK_FIELD_NAME = 'bank_code';

const NO_MATCH_MESSAGE: Record<string, { id: string; defaultMessage: string }> = {
  bank_code: {
    id: 'beneficiary.modal.bank.noMatches',
    defaultMessage: 'No banks matching your search',
  },
};

interface AutocompleteFieldProps {
  field: BeneficiaryField;
  control: Control<FormValues>;
  // Current form values, already watched by the modal. Passed in rather than
  // watched here so this component holds no subscription of its own.
  values: FormValues;
  disabled: boolean;
  hasError: boolean;
  helperText?: React.ReactNode;
  // Called when this field commits a new value, so the form can clear any field
  // scoped to it. Synchronous with the commit — a watch-based effect races the
  // re-render that re-resolves the child against its new option list.
  onValueCommitted?: (fieldName: string) => void;
}

const measureInput = (el: HTMLElement) => el.querySelector('input');

const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  field,
  control,
  values,
  disabled,
  hasError,
  helperText,
  onValueCommitted,
}) => {
  const { formatMessage } = useIntl();

  const parentName = getParentFieldName(field);
  // `FormValues` permits `string[]` (multi-select). Narrow at runtime rather than
  // casting: an array is truthy, so a `dependsOn` pointing at a multi-select would
  // read as "parent chosen", picking the wrong empty state and a junk remount key.
  const parentValue = parentName
    ? pipe(
        Option.fromNullable(values[parentName]),
        Option.filter((value): value is string => typeof value === 'string'),
        Option.getOrElse(() => ''),
      )
    : undefined;

  // Load-bearing, not an optimisation: a fresh array identity every render makes
  // the virtualized listbox rebuild.
  const scopedOptions = useMemo(
    () => withLogoSrc(getScopedOptions(field, parentName ? { [parentName]: parentValue ?? '' } : {})),
    [field, parentName, parentValue],
  );
  const hasLogos = useMemo(
    () => field.name === BANK_FIELD_NAME || hasOptionLogos(scopedOptions),
    [field.name, scopedOptions],
  );

  const currentValue = pipe(
    Option.fromNullable(values[field.name]),
    Option.filter((value): value is string => typeof value === 'string'),
    Option.getOrElse(() => ''),
  );
  const branchEntry = useBankBranchEntry({
    fieldName: field.name,
    options: scopedOptions,
    value: currentValue,
    parentValue: parentValue ?? '',
  });
  const { options } = branchEntry;

  // Three distinct empty states, which all otherwise read as the shared generic
  // "No options": no parent chosen yet, parent chosen but genuinely childless,
  // and (for an unscoped field) a search that matched nothing.
  const noOptionsText = useMemo(() => {
    if (!parentName) {
      // Never return undefined: `AutocompleteVirtualizedBase` spreads caller props
      // AFTER its own localised `noOptionsText`, so an undefined here would wipe it
      // and fall through to MUI's hardcoded English default.
      return pipe(
        Option.fromNullable(NO_MATCH_MESSAGE[field.name]),
        Option.map((message) => formatMessage(message)),
        Option.getOrElse(() => formatMessage({ id: 'options.noOptionsText', defaultMessage: 'No options' })),
      );
    }
    if (branchEntry.noMatchText) return branchEntry.noMatchText;
    return parentValue
      ? formatMessage({
          id: 'beneficiary.modal.branch.noBranches',
          defaultMessage: 'No branches found for this bank',
        })
      : formatMessage({
          id: 'beneficiary.modal.branch.selectBankFirst',
          defaultMessage: 'Select a bank first',
        });
  }, [parentName, parentValue, field.name, formatMessage, branchEntry.noMatchText]);

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: f }) => {
        const current = typeof f.value === 'string' ? f.value : '';

        // Resolve against the SCOPED list only. Branch codes repeat across banks
        // ("001" exists under several), so resolving against the unscoped list
        // would display another bank's branch label.
        const value: BeneficiaryFieldOption | null = branchEntry.findSelected(current);

        const textFieldProps = {
          label: field.label,
          required: true,
          size: 'small' as const,
          error: hasError,
          helperText,
          // Feed Controller's blur back to react-hook-form. Without it the
          // field is never marked touched, and since the form validates on
          // `onTouched` an empty required autocomplete would never show its
          // error — unlike the plain text fields, which get onBlur from
          // `register`.
          onBlur: f.onBlur,
        };

        const renderInput = (params: AutocompleteRenderInputParams) => (
          <TruncatedTooltip title={value?.label ?? ''} measure={measureInput}>
            <TextField
              {...params}
              {...textFieldProps}
              InputProps={{
                ...params.InputProps,
                startAdornment:
                  hasLogos && value ? (
                    <InputAdornment position="start">
                      <Avatar
                        alt={value.label}
                        src={pipe(getOptionLogo(value), Option.getOrUndefined)}
                        sx={{ width: 24, height: 24 }}
                      />
                    </InputAdornment>
                  ) : (
                    params.InputProps.startAdornment
                  ),
              }}
            />
          </TruncatedTooltip>
        );

        // Every field gets the truncation tooltip — long branch names clip just
        // like bank names. Only the avatar depends on the field having logos.
        const listboxProps = { suppressAvatar: !hasLogos, suppressToolTip: false, tooltipOnTruncation: true };

        return (
          <AutocompleteVirtualizedBase
            // Remount when the parent's value changes. MUI keeps its internal
            // input text when `value` is cleared programmatically, so without
            // this the branch box still displays the previous bank's branch
            // while the form value is empty — the user sees a filled field and
            // a disabled submit with no explanation.
            key={parentName ? `${field.name}:${parentValue ?? ''}` : field.name}
            fullWidth
            options={options}
            value={value}
            disabled={disabled}
            noOptionsText={noOptionsText}
            getOptionLabel={(option: BeneficiaryFieldOption) => option.label}
            // Store the option's `value`, never its label.
            onChange={(_: unknown, option: BeneficiaryFieldOption | null) => {
              const next = option?.value ?? '';
              f.onChange(next);
              if (next !== current) onValueCommitted?.(field.name);
            }}
            {...branchEntry.autocompleteProps}
            textFieldProps={textFieldProps}
            renderInput={renderInput}
            slotProps={{ listbox: { ...listboxProps, maxVisibleItems: MAX_VISIBLE_OPTIONS } }}
          />
        );
      }}
    />
  );
};

export default AutocompleteField;
